import { type DashboardFunctions } from '..';
import type {
  EncoderConfig,
  EncoderStatus,
  GameClipConfigState,
  HostQrCodeParameters,
  PlaycastHostEdition,
  PlayerQrCodeParameters,
} from '@playcastdotio/CoreTypes';
import {
  HostSettingsPayload,
  Invitation,
  PeerIdType,
  PeerMessage,
  PeerMessageType,
  PlaycastMessage,
  PlaycastRemoteMessages,
  PlaycastStateMessages,
  Profile,
  QueueSettingsBody,
  generators,
  websocket,
} from '@playcastdotio/Messaging';
import { makeRemoteControlMessagingObject } from '../messaging/protocols';
import { handleIncomingRemoteMessage, handleIncomingStateMessage } from '../messaging/handlers/rcClient';
import { destroyAllSharedStores, getStateStore } from '../stores';
import { AfkPreventionState, functionConnectionState, iceServerState, remoteHostList } from '../stores/shared';
import { createRemoteControlClient } from '@playcastdotio/Peer';
import { getRemoteClientStore } from '../stores/remoteClientStores';
import { isAppleDevice } from '../util/isApple';
import { sendMessage } from './remoteClientRealtime';

let currentRemoteHostState = remoteHostList.currentState;
remoteHostList.addListener((state) => {
  currentRemoteHostState = state;
  setTimeout(() => {
    if (Object.keys(currentRemoteHostState.hosts).length > 0 && currentRemoteHostState.selectedHostId === '') {
      // auto select the first host
      const firstHost = Object.keys(currentRemoteHostState.hosts)[0];
      const firstHostData = currentRemoteHostState.hosts[firstHost];
      remoteHostList.updateState({
        ...currentRemoteHostState,
        selectedHostId: firstHostData.remoteId,
      });
    }
  }, 10);
});

let currentRemoteControlTarget = '';
let remoteInterface: DashboardFunctions;
let rcClient: ReturnType<typeof createRemoteControlClient>;
export const getRemoteControlInterface = async (remoteControlHostId: string): Promise<DashboardFunctions> => {
  if (currentRemoteControlTarget === remoteControlHostId) {
    return remoteInterface;
  }

  if (remoteControlHostId !== '') {
    functionConnectionState.updateState({
      ...functionConnectionState.currentState,
      connected: false,
      initializing: false,
      functionKey: remoteControlHostId,
      error: false,
    });
  }
  if (rcClient && remoteControlHostId !== currentRemoteControlTarget) {
    console.log('[RC-Fns] Closing existing remote client');
    rcClient.close('rc:close:duplicate_client');
  }
  destroyAllSharedStores(false, ['remoteHostList']);
  currentRemoteControlTarget = remoteControlHostId;
  const queue: PlaycastMessage<PlaycastRemoteMessages>[] = [];
  const messaging: ReturnType<typeof makeRemoteControlMessagingObject> = {
    send: (msg) => {
      queue.push(msg);
    },
    receive: (cb) => {
      console.log('[RC-Fns] No messaging connection, cannot receive messages.');
    },
  };
  if (remoteControlHostId !== '') {
    console.log('[RC-Fns] Creating remote client');

    const remoteClientId = await new Promise<string>((resolve, reject) => {
      sendMessage(websocket.generators.peer(PeerMessageType.PEER_GET_ID, { peerIdType: PeerIdType.REMOTE_CLIENT }));
      const timeout = setTimeout(() => {
        reject('Timeout');
      }, 5000);
      const listener = functionConnectionState.addListener((state) => {
        if (state.remoteClientId !== '') {
          clearTimeout(timeout);
          functionConnectionState.removeListener(listener);
          resolve(state.remoteClientId);
        }
      });
    });

    const iceServers = iceServerState.currentState.remoteClientIceServers;

    const store = getStateStore(remoteControlHostId);
    rcClient = createRemoteControlClient(
      {
        realtimeApiManager: {
          send: (message) => {
            sendMessage(message);
          },
        },
        additionalMetadata: {
          isAppleDevice: isAppleDevice(),
        },
        iceServers,
        forceTurn: !iceServerState.currentState.allowPeerConnections,
      },
      remoteClientId,
    );
    rcClient.onMessage((message) => {
      if (message.header.target === 'remote') {
        handleIncomingRemoteMessage(message as PlaycastMessage<PlaycastRemoteMessages>, store);
      }
      if (message.header.target === 'state') {
        handleIncomingStateMessage(message as PlaycastMessage<PlaycastStateMessages>, store);
      }
    });
    rcClient.connect(remoteControlHostId);
    let clientStatus = rcClient.status.currentState;
    rcClient.status.addListener((status) => {
      if (clientStatus !== status) {
        console.log('[RC-Fns] Client status changed', status);
        if (status.connectionState === 'closed' || status.connectionState === 'refused') {
          const isError = status.connectionState === 'refused';
          functionConnectionState.updateState({
            ...functionConnectionState.currentState,
            initializing: false,
            connected: false,
            error: isError,
          });
        }
        clientStatus = status;
      }
    });

    rcClient.status.addListener((status) => {
      if (status.connectionState === 'closed' || status.connectionState === 'refused') {
        const isError = status.connectionState === 'refused';
        functionConnectionState.updateState({
          ...functionConnectionState.currentState,
          functionKey: remoteControlHostId,
          initializing: false,
          connected: false,
          error: isError,
        });
      }
      if (status.connectionState === 'connected') {
        functionConnectionState.updateState({
          ...functionConnectionState.currentState,
          functionKey: remoteControlHostId,
          initializing: false,
          connected: true,
          error: false,
        });
      }
    });
    if (rcClient.status.currentState.connectionState === 'connected') {
      functionConnectionState.updateState({
        ...functionConnectionState.currentState,
        functionKey: remoteControlHostId,
        initializing: false,
        connected: true,
        error: false,
      });
    }
    rcClient.incomingTracks.addListener((tracks) => {
      const trackIds = Object.keys(tracks);
      if (trackIds.length > 0) {
        // get a map of metadata.channel -> MediaStreamTrack
        // const raw: { [channel: string]: MediaStreamTrack } = {};
        // trackIds.forEach((trackId) => {
        //   const track = tracks[trackId];
        //   raw[track.metadata.channel] = track.track;
        // });

        // const rcs = getRemoteClientStore();
        // rcs.previewTrackState.updateState({
        //   ...rcs.previewTrackState.currentState,
        //   raw,
        // });

        const mediaStream = new MediaStream();
        trackIds.forEach((trackId) => {
          mediaStream.addTrack(tracks[trackId].track);
        });
        getRemoteClientStore().previewStreamState.updateState(mediaStream);
      }
    });
    rcClient.statistics.addListener((stats) => {
      // console.log('Remote stats', stats);
    });

    messaging.send = (msg) => {
      rcClient.sendMessage(msg);
    };
  }

  const sendFunctionCommand = (targetFnName: keyof DashboardFunctions, ...args: unknown[]) => {
    const message = generators.remote.sendFunctionCommand(targetFnName, args);
    messaging.send(message);
  };

  const state = getStateStore(remoteControlHostId);
  getRemoteClientStore().previewTrackState.addListener((tracks) => {
    state.audioState.updateState(state.audioState.currentState);
  });

  remoteInterface = {
    startStream: () => {
      // Note: Broadcast Started analytics are sent by the Electron app when it detects status change
      sendFunctionCommand('startStream');
    },
    stopStream: (reason?: string) => {
      // Note: Broadcast Ended analytics are sent by the Electron app when it detects status change
      sendFunctionCommand('stopStream', reason ?? 'rpc:stopstream');
    },
    setStreamTitle: (title: string) => {
      sendFunctionCommand('setStreamTitle', title);
    },
    showVideoPreview: (show?: boolean) => {
      sendFunctionCommand('showVideoPreview', show);
    },
    changeDisplay: (displayId: number) => {
      sendFunctionCommand('changeDisplay', displayId);
    },
    setOutboundVideoDevice: (deviceId: string, windowCapturePid?: number) => {
      sendFunctionCommand('setOutboundVideoDevice', deviceId, windowCapturePid);
    },
    setMicrophone: (deviceId: string) => {
      sendFunctionCommand('setMicrophone', deviceId);
    },
    setMicrophoneMuted: (muted: boolean) => {
      sendFunctionCommand('setMicrophoneMuted', muted);
    },
    setMicrophoneVolume: (volume: number) => {
      sendFunctionCommand('setMicrophoneVolume', volume);
    },
    setDesktopAudioMuted: (muted: boolean) => {
      sendFunctionCommand('setDesktopAudioMuted', muted);
    },
    setDesktopAudioVolume: (volume: number) => {
      sendFunctionCommand('setDesktopAudioVolume', volume);
    },
    setDesktopAudio: (deviceId: string) => {
      sendFunctionCommand('setDesktopAudio', deviceId);
    },
    kickPlayer: (playerId: string) => {
      sendFunctionCommand('kickPlayer', playerId);
    },
    kickAll: () => {
      sendFunctionCommand('kickAll');
    },
    setAllowedGamepadInput: (allowed: boolean, playerId?: string) => {
      sendFunctionCommand('setAllowedGamepadInput', allowed, playerId);
    },
    setAllowedKeyboardInput: (allowed: boolean, playerId?: string) => {
      sendFunctionCommand('setAllowedKeyboardInput', allowed, playerId);
    },
    setAllowedMouseInput: (allowed: boolean, playerId?: string) => {
      sendFunctionCommand('setAllowedMouseInput', allowed, playerId);
    },
    setAllowedTouchInput: (allowed: boolean, playerId?: string) => {
      sendFunctionCommand('setAllowedTouchInput', allowed, playerId);
    },
    setDisplaySurfing: (enabled: boolean) => {
      console.log('[RC-Fns] Enabling surfing', enabled);
      sendFunctionCommand('setDisplaySurfing', enabled);
    },
    startUpdate: () => {
      console.error('[RC-Fns] Not available for remote control.');
    },
    ignoreUpdate: () => {
      console.error('[RC-Fns] Not available for remote control.');
    },
    setStaticImage: (imageBase64Blob: string, playerId?: string) => {
      sendFunctionCommand('setStaticImage', imageBase64Blob, playerId);
    },
    setQrCodeParameters: (qrCodeParameters: PlayerQrCodeParameters) => {
      sendFunctionCommand('setQrCodeParameters', qrCodeParameters);
    },
    setHostQrCodeParameters: (qrCodeParameters: HostQrCodeParameters) => {
      sendFunctionCommand('setHostQrCodeParameters', qrCodeParameters);
    },
    fetchAudioSources: () => {
      sendFunctionCommand('fetchAudioSources');
    },
    setDesktopAudioSource: (sourceId: string, playerId?: string) => {
      sendFunctionCommand('setDesktopAudioSource', sourceId, playerId);
    },
    setGamepadSharing: (gamepadIndex: number, enabled: boolean) => {
      sendFunctionCommand('setGamepadSharing', gamepadIndex, enabled);
    },
    setAssignGamepadsAutomatically: (enabled: boolean) => {
      sendFunctionCommand('setAssignGamepadsAutomatically', enabled);
    },
    pauseInput: (paused: boolean, playerId?: string) => {
      sendFunctionCommand('pauseInput', paused, playerId);
    },
    acceptKnock: (knockId: string) => {
      sendFunctionCommand('acceptKnock', knockId);
    },
    rejectKnock: (knockId: string) => {
      sendFunctionCommand('rejectKnock', knockId);
    },
    reorderLobby: (pendingPlayers: string[]) => {
      sendFunctionCommand('reorderLobby', pendingPlayers);
    },
    updateQueueSettings: (enabled: boolean, settings: QueueSettingsBody, instantConnect: boolean) => {
      sendFunctionCommand('updateQueueSettings', enabled, settings, instantConnect);
    },
    updateLobbySettings: (update: Invitation) => {
      sendFunctionCommand('updateLobbySettings', update);
    },
    updateHostSettings: (settings: HostSettingsPayload) => {
      sendFunctionCommand('updateHostSettings', settings);
    },
    updateInvitationDescription: (description: string) => {
      sendFunctionCommand('updateInvitationDescription', description);
    },
    setGamepadAssignmentEnabled(playerId, gamepadIndex, enabled) {
      sendFunctionCommand('setGamepadAssignmentEnabled', playerId, gamepadIndex, enabled);
    },
    sendInvitationEmail: (email: string, message: string, invitationId?: string) => {
      sendFunctionCommand('sendInvitationEmail', email, message, invitationId);
    },
    sendInvitationSms: (phone: string, message: string, invitationId?: string) => {
      sendFunctionCommand('sendInvitationSms', phone, message, invitationId);
    },
    renameHost: (newName: string, remoteId: string) => {
      // get info from remoteHostList about remoteId.
      type RemoteData =
        | {
            machineId: string;
            environment: string;
            version: string;
            uniqueId: string;
          }
        | undefined;
      const allActiveRemotes = Object.values(currentRemoteHostState.hosts);
      console.warn('All active remotes', allActiveRemotes);
      const allInactiveRemotes = Object.values(currentRemoteHostState.inactive);
      let remote: RemoteData = allActiveRemotes.find((host) => host.remoteId === remoteId);
      if (!remote) {
        remote = allInactiveRemotes.find((host) => host.remoteId === remoteId);
      }
      if (!remote) {
        console.error('[IPC-Fns] Remote not found');
        return;
      } else {
        console.error('[IPC-Fns] Remote found, but not implemented');
        // updateRemoteHostName(newName, remote.machineId, remote.environment, remote.version, remote.uniqueId);
      }
    },
    setFilterGamepadButtons: (filtered: boolean) => {
      sendFunctionCommand('setFilterGamepadButtons', filtered);
    },
    setMaxGamepadAssignments: (maxAssignments: number) => {
      sendFunctionCommand('setMaxGamepadAssignments', maxAssignments);
    },
    setWebrtcPresetGroup: (presetGroup) => {
      sendFunctionCommand('setWebrtcPresetGroup', presetGroup);
    },
    setWebrtcShouldHandlePanic: (shouldHandlePanic) => {
      sendFunctionCommand('setWebrtcShouldHandlePanic', shouldHandlePanic);
    },
    processLaunch: (launchId, argument) => {
      sendFunctionCommand('processLaunch', launchId, argument);
    },
    processRestart: (argument) => {
      sendFunctionCommand('processRestart', argument);
    },
    processTerminate: (type) => {
      sendFunctionCommand('processTerminate', type);
    },
    systemLogOff: (username) => {
      sendFunctionCommand('systemLogOff', username);
    },
    processSetPolicy: (
      nonFocusInput,
      constrainMouse,
      allowToolbarAccess,
      forceProcessOnTop,
      launchTimeout,
      captureAudio,
      captureVideo,
      saveEnumeratedApps,
      onlyExitOnDisconnect,
      enableHostDominanceKick,
    ) => {
      sendFunctionCommand(
        'processSetPolicy',
        nonFocusInput,
        constrainMouse,
        allowToolbarAccess,
        forceProcessOnTop,
        launchTimeout,
        captureAudio,
        captureVideo,
        saveEnumeratedApps,
        onlyExitOnDisconnect,
        enableHostDominanceKick,
      );
    },
    processGetPolicy: () => {
      sendFunctionCommand('processGetPolicy');
    },
    overlayCreateImageOverlay: (overlayName, destName, imagePath, translate, rotate, scale, visible) => {
      console.log('[RC-Fns] Creating image overlay', overlayName, destName, imagePath, translate, rotate, scale, visible);
      sendFunctionCommand('overlayCreateImageOverlay', overlayName, destName, imagePath, translate, rotate, scale, visible);
    },
    createQrCodeOverlay: async () => {
      console.log('[RC-Fns] Creating QR code overlay');
      sendFunctionCommand('createQrCodeOverlay');
    },
    overlayDeleteOverlay: (overlayName) => {
      sendFunctionCommand('overlayDeleteOverlay', overlayName);
    },
    updateTwitchSettings: (settings) => {
      sendFunctionCommand('updateTwitchSettings', settings);
    },
    updateDiscordSettings: (settings) => {
      sendFunctionCommand('updateDiscordSettings', settings);
    },
    processEnumRunning: (captureElementName, captureElementRequestId) => {
      if (captureElementName === undefined) sendFunctionCommand('processEnumRunning');
      else sendFunctionCommand('processEnumRunning', captureElementName, captureElementRequestId);
    },
    processSelect: (processId: number) => {
      sendFunctionCommand('processSelect', processId);
    },
    processDisconnect: () => {
      sendFunctionCommand('processDisconnect');
    },
    setAllowPeerConnections: (allowPeerConnections: boolean) => {
      sendFunctionCommand('setAllowPeerConnections', allowPeerConnections);
    },
    updateQrCode: (base64QRCode) => {
      sendFunctionCommand('updateQrCode', base64QRCode);
    },
    updateShowStreamQrCode: (showStreamQrCode: boolean) => {
      sendFunctionCommand('updateShowStreamQrCode', showStreamQrCode);
    },
    setEncoderConfig: (config: EncoderConfig) => {
      sendFunctionCommand('setEncoderConfig', config);
    },
    updateGameClipConfig: (config: GameClipConfigState) => {
      sendFunctionCommand('updateGameClipConfig', config);
    },
    updateGameClipStatus: (status: EncoderStatus) => {
      sendFunctionCommand('updateGameClipStatus', status);
    },
    setAutomaticUpdatesEnabled: (enabled: boolean) => {
      sendFunctionCommand('setAutomaticUpdatesEnabled', enabled);
    },
    getAutomaticUpdatesEnabled: () => {
      sendFunctionCommand('getAutomaticUpdatesEnabled');
    },
    changeEdition: (edition: PlaycastHostEdition) => {
      sendFunctionCommand('changeEdition', edition);
    },
    queryProfile: () => {
      sendFunctionCommand('queryProfile');
    },
    queryOwnerAliases: () => {
      sendFunctionCommand('queryOwnerAliases');
    },
    queryProfileInvitations: (cursor?: string, limit?: number, searchTerm?: string) => {
      sendFunctionCommand('queryProfileInvitations', cursor, limit, searchTerm);
    },
    updateProfile: (profile: Profile) => {
      sendFunctionCommand('updateProfile', profile);
    },
    profileCreateMatchmakerLobby: () => {
      sendFunctionCommand('profileCreateMatchmakerLobby');
    },
    profileJoinMatchmakerLobby: () => {
      sendFunctionCommand('profileJoinMatchmakerLobby');
    },
    profileLeaveMatchmakerLobby: () => {
      sendFunctionCommand('profileLeaveMatchmakerLobby');
    },
    profileStartMatchmakerLobby: (useSteamGuestLogin?: boolean, regionOverride?: string) => {
      sendFunctionCommand('profileStartMatchmakerLobby', useSteamGuestLogin, regionOverride);
    },
    profileSelectInvitation: (invitationId?: string) => {
      sendFunctionCommand('profileSelectInvitation', invitationId);
    },
    profileDestroyMatchmakerLobby: (selectedInvitationId?: string) => {
      sendFunctionCommand('profileDestroyMatchmakerLobby', selectedInvitationId);
    },
    matchmakerKickPlayer: (playerId: string) => {
      sendFunctionCommand('matchmakerKickPlayer', playerId);
    },
    sendTwitchBot: (invitationId: string) => {
      sendFunctionCommand('sendTwitchBot', invitationId);
    },
    sendDiscordBot: (invitationId: string) => {
      sendFunctionCommand('sendDiscordBot', invitationId);
    },
    setWindowState: (windowState) => {
      sendFunctionCommand('setWindowState', windowState);
    },
    toggleEncoder: () => {
      sendFunctionCommand('toggleEncoder');
    },
    setAfkPreventionEnabled: (enabled: boolean) => {
      sendFunctionCommand('setAfkPreventionEnabled', enabled);
    },
    setAfkPreventionSettings: (settings: AfkPreventionState) => {
      sendFunctionCommand('setAfkPreventionSettings', settings);
    },
    testAfkPreventionKey: (keyCode: number, keyDisplayName: string) => {
      sendFunctionCommand('testAfkPreventionKey', keyCode, keyDisplayName);
    },
    shadowDisplayAddDisplay: (width: number, height: number, refreshRate: number, deviceName: string, serialNumber: string, guid?: string) => {
      sendFunctionCommand('shadowDisplayAddDisplay', width, height, refreshRate, deviceName, serialNumber, guid);
    },
    shadowDisplayRemoveDisplay: (guid: string) => {
      sendFunctionCommand('shadowDisplayRemoveDisplay', guid);
    },
    shadowDisplayRemoveAllDisplays: () => {
      sendFunctionCommand('shadowDisplayRemoveAllDisplays');
    },
    shadowDisplayGetDisplays: () => {
      sendFunctionCommand('shadowDisplayGetDisplays');
    },
    shadowDisplayGetDriverStatus: () => {
      sendFunctionCommand('shadowDisplayGetDriverStatus');
    },
    shadowDisplayPingDriver: () => {
      sendFunctionCommand('shadowDisplayPingDriver');
    },
    shadowDisplaySetRenderAdapter: (adapterLuid: { lowPart: number; highPart: number }) => {
      sendFunctionCommand('shadowDisplaySetRenderAdapter', adapterLuid);
    },
    shadowDisplayGetWatchdog: () => {
      sendFunctionCommand('shadowDisplayGetWatchdog');
    },
    shadowDisplayGetProtocolVersion: () => {
      sendFunctionCommand('shadowDisplayGetProtocolVersion');
    },
    // Machine ID Attestation (not available in remote control mode)
    attestMachineId: async () => {
      console.warn('[RC-Fns] Machine ID attestation not available in remote control mode');
      throw new Error('Machine ID attestation requires local Electron instance');
    },
    checkAttestationStatus: () => {
      console.warn('[RC-Fns] Machine ID attestation status not available in remote control mode');
      return { status: 'unavailable', error: 'Remote control mode' };
    },
  };
  return remoteInterface;
};

export const rcClientHandlePeerMessage = (message: PeerMessage) => {
  if (rcClient) {
    rcClient.handlePeerMessage(message);
  } else {
    console.error('rcClient not initialized');
  }
};

// export const destroyRemoteInterface = () => {
//   if (currentRemoteControlTarget !== '') {
//     destroyAllSharedStores(false);
//     currentRemoteControlTarget = '';
//   }
// };
